"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { useTranslation } from "~/app/hooks/useTranslation";
import useLocalizedHref from "~/app/hooks/useLocalizedHref";
import HomeIconSvg from "~/assets/images/helpCenter/home.svg";
import { getBreadcrumbItems } from "../lib/breadcrumbs";

import styles from "./Breadcrumb.module.scss";

export default function Breadcrumb({ sections, segments }) {
  const pathname = usePathname();
  const { locale, buildHref } = useLocalizedHref();
  const { t } = useTranslation();
  const items = getBreadcrumbItems(locale, pathname, segments)
    .map((item) => ({ ...item, label: t(sections?.[item.label] ?? item.label) }));
  const homeHref = buildHref("");
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [{ label: "Home", href: homeHref }, ...items.filter((item) => item.href)]
      .map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.label,
        item: `https://palmate.ai${item.href}`,
      })),
  };

  return (
    <nav aria-label="breadcrumb" className={styles.breadcrumb}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema).replace(/</g, "\\u003c") }} />
      <ol>
        <li>
          <Link href={homeHref} aria-label="Home">
            <HomeIconSvg className={styles.homeIcon} aria-hidden="true" />
          </Link>
        </li>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={index} className={isLast ? styles.active : ""}>
              {isLast || !item.href
                ? <span aria-current={isLast ? "page" : undefined}>{item.label}</span>
                : <Link href={item.href}>{item.label}</Link>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
